// frontend/src/app/components/projects/project-corpus/provenance-strip.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { ImportSourceType, ProjectSummary } from '../../../project-corpus/domain/types';
import { Project } from '../../../dtos/project';
import { ProjectProtocol } from '../../../dtos/project-protocol';

interface SourceChip {
  readonly source: ImportSourceType;
  readonly label: string;
  readonly count: number;
}

export type ReadinessStatus = 'blocked' | 'warning' | 'ready';

export interface ReadinessCheck {
  readonly status: ReadinessStatus;
  readonly papersComplete: boolean;
  readonly criteriaDefined: boolean;
  readonly protocolFilled: boolean;
  readonly allReady: boolean;
  readonly totalPapers: number;
  readonly needsAttention: number;
}

export interface PopoverData {
  readonly x: number;
  readonly y: number;
  readonly title: string;
  readonly body: string;
}

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  OPENALEX: 'OpenAlex',
  BIBTEX: 'BibTeX',
  PDF: 'PDF',
  GROUP_CORPUS: 'Group corpus',
};

@Component({
  selector: 'app-provenance-strip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './provenance-strip.component.html',
  styleUrls: ['./provenance-strip.component.scss'],
})
export class ProvenanceStripComponent {
  private readonly _summary = signal<ProjectSummary | null>(null);

  @Input()
  set summary(value: ProjectSummary | null) {
    this._summary.set(value);
  }
  get summary(): ProjectSummary | null {
    return this._summary();
  }

  @Output() needsAttentionToggle = new EventEmitter<void>();

  private readonly _projectDetails = signal<Project | null>(null);
  private readonly _protocol = signal<ProjectProtocol | null>(null);

  @Input()
  set projectDetails(value: Project | null) {
    this._projectDetails.set(value);
  }

  @Input()
  set protocol(value: ProjectProtocol | null) {
    this._protocol.set(value);
  }

  private readonly _canAdvance = signal(false);
  /** Signal used in the template — true while import jobs are running. */
  readonly _suppressNeedsAttention = signal(false);

  /** When true, the advance button becomes clickable even if not all metadata is ready. */
  @Input()
  set canAdvance(value: boolean) {
    this._canAdvance.set(value);
  }

  @Input()
  set suppressNeedsAttention(value: boolean) {
    this._suppressNeedsAttention.set(value);
  }

  /** Whether to show the PI-only "Start abstract screening" button (set by the parent). */
  @Input() showAdvanceButton = false;
  @Output() advance = new EventEmitter<void>();

  /** The advance button enabled state: enabled when there are no running jobs and the status is not blocked. */
  get advanceEnabled(): boolean {
    return this._canAdvance() && this.readiness().status !== 'blocked';
  }

  onAdvanceClick(): void {
    this.advance.emit();
  }

  readonly currentSummary = this._summary.asReadonly();

  readonly readiness = computed<ReadinessCheck>(() => {
    const s = this._summary();
    const p = this._projectDetails();
    const proto = this._protocol();

    const papersComplete = s ? s.itemsNeedingAttention === 0 && s.totalPapers > 0 : false;
    const criteriaDefined = p
      ? this.hasCriteria(p.inclusionCriteria) || this.hasCriteria(p.exclusionCriteria)
      : false;
    const protocolFilled = proto ? !!proto.researchQuestions?.trim() : false;

    let status: ReadinessStatus = 'ready';
    if (s?.totalPapers === 0 || !criteriaDefined || !protocolFilled) {
      status = 'blocked';
    } else if (s && s.itemsNeedingAttention > 0) {
      status = 'warning';
    }

    return {
      status,
      papersComplete,
      criteriaDefined,
      protocolFilled,
      allReady: papersComplete && criteriaDefined && protocolFilled,
      totalPapers: s?.totalPapers ?? 0,
      needsAttention: s?.itemsNeedingAttention ?? 0,
    };
  });

  readonly completionNote = computed(() => {
    const s = this._summary();
    if (!s) return '';
    if (s.state !== 'SETUP' && s.state !== 'INGESTION') return '';
    if (s.totalPapers === 0) {
      return 'No papers imported yet. Add papers to the corpus before starting screening.';
    }
    if (this._suppressNeedsAttention()) {
      return 'Import in progress. Metadata checks will appear after the import completes.';
    }
    if (s.itemsNeedingAttention > 0) {
      const n = s.itemsNeedingAttention;
      return `${n} paper${n === 1 ? '' : 's'} need a title, abstract, or authors. If you advance now incomplete papers will be removed.`;
    }
    if (this.readiness().allReady) return 'Ready to finish ingestion.';
    return '';
  });

  readonly sourceChips = computed<SourceChip[]>(() => {
    const s = this._summary();
    if (!s) return [];
    return (Object.keys(SOURCE_LABELS) as ImportSourceType[])
      .filter((src) => s.perSource[src] > 0)
      .map((src) => ({
        source: src,
        label: SOURCE_LABELS[src],
        count: s.perSource[src],
      }));
  });

  readonly popover = signal<PopoverData | null>(null);

  showPopover(event: MouseEvent, pill: string): void {
    const chk = this.readiness();
    const s = this._summary();
    if (!s) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    let title = '';
    let body = '';

    switch (pill) {
      case 'status':
        if (chk.status === 'blocked') {
          title = 'Cannot advance';
          body =
            'One or more requirements are not met. Define screening criteria and complete the Systematic Review Protocol before advancing.';
        } else if (chk.status === 'warning') {
          title = 'Can advance';
          body =
            'All requirements are defined but some papers lack metadata. Advancing will remove incomplete papers.';
        } else {
          title = 'Ready to advance';
          body =
            'All requirements are met. Papers are complete, screening criteria are defined, and the protocol is filled.';
        }
        break;
      case 'papers':
        title = 'Paper metadata';
        body = chk.papersComplete
          ? 'All papers have complete metadata (title, abstract, and authors).'
          : `${chk.needsAttention} paper${chk.needsAttention === 1 ? '' : 's'} missing title, abstract, or authors. Incomplete papers will be removed if you advance.`;
        break;
      case 'criteria':
        title = 'Screening criteria';
        body = chk.criteriaDefined
          ? 'Inclusion and exclusion criteria have been defined for screening.'
          : 'No screening criteria defined yet. Go to Setup to define inclusion and exclusion criteria.';
        break;
      case 'protocol':
        title = 'Systematic Review Protocol';
        body = chk.protocolFilled
          ? 'The Systematic Review Protocol is complete.'
          : 'The Systematic Review Protocol is not yet filled. Go to Setup to complete it.';
        break;
    }

    this.popover.set({ x: rect.left + rect.width / 2, y: rect.bottom + 8, title, body });
  }

  hidePopover(): void {
    this.popover.set(null);
  }

  onNeedsAttentionClick(): void {
    this.needsAttentionToggle.emit();
  }

  private hasCriteria(val: string | undefined | null): boolean {
    if (!val) return false;
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.length > 0;
      return false;
    } catch {
      return val.trim().length > 0;
    }
  }
}
